import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatCardModule } from '@angular/material/card';
import { DecimalPipe } from '@angular/common';

import { COMMON_I18N } from '@app/i18n';
import { TOMASS_SUMMARY_I18N } from '../tomass-summary.i18n';
import type { FrequencyRow } from '../tomass-summary.types';

@Component({
  selector: 'app-tomass-frequency-table',
  templateUrl: './tomass-frequency-table.component.html',
  styleUrls: ['./tomass-frequency-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    DecimalPipe,
  ],
})
export class TomassFrequencyTableComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly i18n = TOMASS_SUMMARY_I18N.frequencyTable;
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  readonly rows = input<FrequencyRow[]>([]);
  readonly selectedDateKey = input<string | null>(null);
  readonly rowSelection = output<FrequencyRow | null>();
  readonly pageSizeOptions = [5, 10, 25, 100];

  readonly displayedColumns = [
    'dateLabel',
    'measurementCount',
    'accountList',
    'estimatedRegion',
  ];

  readonly dataSource = computed(() => {
    const ds = new MatTableDataSource(this.rows());
    ds.sortingDataAccessor = (row, columnName) => this.getSortValue(row, columnName);
    if (this._sort) {
      ds.sort = this._sort;
    }
    if (this._paginator) {
      ds.paginator = this._paginator;
    }
    return ds;
  });

  readonly totalMeasurementCount = computed(() =>
    this.rows().reduce((sum, row) => sum + row.measurementCount, 0),
  );

  @ViewChild(MatSort) private _sort?: MatSort;
  @ViewChild(MatPaginator) private _paginator?: MatPaginator;
  private readonly beforePrintHandler = (): void => {
    this.setPrintMode(true);
  };
  private readonly afterPrintHandler = (): void => {
    this.setPrintMode(false);
  };

  ngOnInit(): void {
    this.configurePaginatorLabels();
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeprint', this.beforePrintHandler);
      window.addEventListener('afterprint', this.afterPrintHandler);
    }
  }

  ngAfterViewInit(): void {
    const ds = this.dataSource();
    if (this._sort) {
      ds.sort = this._sort;
    }
    if (this._paginator) {
      ds.paginator = this._paginator;
      this._paginator.pageSize = 10;
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeprint', this.beforePrintHandler);
      window.removeEventListener('afterprint', this.afterPrintHandler);
    }
  }

  formatAccountList(row: FrequencyRow): string {
    if (row.accountNames.length === 0) {
      return '-';
    }
    return row.accountNames.join(', ');
  }

  formatEstimatedRegion(row: FrequencyRow): string {
    if (row.estimatedRegions.length === 0) {
      return '-';
    }
    return row.estimatedRegions.join(' / ');
  }

  isRowSelected(row: FrequencyRow): boolean {
    return this.selectedDateKey() === row.dateKey;
  }

  onRowClick(row: FrequencyRow): void {
    if (this.isRowSelected(row)) {
      this.rowSelection.emit(null);
      return;
    }
    this.rowSelection.emit(row);
  }

  private getSortValue(row: FrequencyRow, columnName: string): number | string {
    if (columnName === 'dateLabel') {
      return row.dateKey;
    }
    if (columnName === 'measurementCount') {
      return row.measurementCount;
    }
    if (columnName === 'accountList') {
      return row.accountNames.join(', ');
    }
    if (columnName === 'estimatedRegion') {
      return row.estimatedRegions.join(' / ');
    }
    return '';
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return `0 / ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} - ${endIndex} / ${length}`;
    };
    this.paginatorIntl.changes.next();
  }

  private setPrintMode(enabled: boolean): void {
    const ds = this.dataSource();
    if (enabled) {
      ds.paginator = null;
      return;
    }

    if (this._paginator) {
      ds.paginator = this._paginator;
      if (!this._paginator.pageSize) {
        this._paginator.pageSize = 10;
      }
    }
  }
}
